/**
 * Shop Profile Dropdown Web Component
 * Displays user avatar with dropdown menu for account actions
 *
 * Attributes:
 * - user-email: User email to display in dropdown
 * - account-url: URL to user's account/wallet page
 * - logout-url: URL for logout action
 * - csrf-token: CSRF token for logout form
 * - open: Boolean attribute to control open/close state
 * - anchor: CSS selector of an existing trigger. The component then renders no avatar
 *   button of its own: the menu is fixed under the anchor, right-aligned to it, and the
 *   anchor carries aria-expanded and aria-controls. Opening moves focus to the first
 *   menu item, and focus leaving both the menu and the anchor closes it.
 */

import { escapeHTML } from "../../../shared/escape_html.js";

const ShopProfileDropdown = (function () {
  const TAG_NAME = "shop-profile-dropdown";
  const MENU_GAP_PX = 8;

  class ShopProfileDropdown extends HTMLElement {
    static get observedAttributes() {
      return [
        "user-name",
        "user-email",
        "account-url",
        "cash-back-href",
        "saved-href",
        "logout-url",
        "csrf-token",
        "anchor",
        "open",
      ];
    }

    constructor() {
      super();
      this._isOpen = false;
    }

    get _anchor() {
      const selector = this.getAttribute("anchor");
      return selector ? document.querySelector(selector) : null;
    }

    connectedCallback() {
      if (!this._initialized) {
        this.render();
        this._initialized = true;
        this._attachEventListeners();
      }
    }

    disconnectedCallback() {
      this._detachEventListeners();
    }

    attributeChangedCallback(name, _oldValue, newValue) {
      if (!this._initialized) return;

      if (name === "open") {
        if (newValue !== null) {
          this._openDropdown();
        } else {
          this._closeDropdown();
        }
      } else {
        this.render();
        this._attachEventListeners();
      }
    }

    _attachEventListeners() {
      const avatarBtn = this.querySelector(".shop-profile-dropdown__avatar");
      if (avatarBtn) {
        avatarBtn.removeEventListener("click", this._onAvatarClick);
        avatarBtn.addEventListener(
          "click",
          (this._onAvatarClick = this._handleAvatarClick.bind(this)),
        );
      }

      // Click outside to close
      document.removeEventListener("click", this._onDocumentClick);
      document.addEventListener(
        "click",
        (this._onDocumentClick = this._handleDocumentClick.bind(this)),
      );

      // Escape key to close
      document.removeEventListener("keydown", this._onEscapeKey);
      document.addEventListener(
        "keydown",
        (this._onEscapeKey = this._handleEscapeKey.bind(this)),
      );

      // Focus leaving the anchored menu closes it; its trigger lives outside the component.
      this.removeEventListener("focusout", this._onFocusOut);
      this.addEventListener(
        "focusout",
        (this._onFocusOut = this._handleFocusOut.bind(this)),
      );
    }

    _detachEventListeners() {
      const avatarBtn = this.querySelector(".shop-profile-dropdown__avatar");
      if (avatarBtn && this._onAvatarClick) {
        avatarBtn.removeEventListener("click", this._onAvatarClick);
      }

      if (this._onDocumentClick) {
        document.removeEventListener("click", this._onDocumentClick);
      }

      if (this._onEscapeKey) {
        document.removeEventListener("keydown", this._onEscapeKey);
      }

      this._stopFollowingAnchor();
    }

    _handleAvatarClick(e) {
      e.stopPropagation();
      const isOpen = this.hasAttribute("open");

      if (isOpen) {
        this.removeAttribute("open");
      } else {
        this.setAttribute("open", "");
      }

      // Dispatch toggle event
      this.dispatchEvent(
        new CustomEvent("profile-dropdown-toggle", {
          bubbles: true,
          composed: true,
          detail: { open: !isOpen },
        }),
      );
    }

    _handleDocumentClick(e) {
      // Close if clicking outside. Exclude clicks on the trigger (the anchor,
      // else the v1 fragno avatar) since nav wiring drives the open/close toggle.
      const trigger =
        this._anchor ||
        document.querySelector(".fragno-global-nav__avatar-trigger");
      if (
        this._isOpen &&
        !this.contains(e.target) &&
        !trigger?.contains(e.target)
      ) {
        this.removeAttribute("open");
      }
    }

    _handleEscapeKey(e) {
      if (e.key === "Escape" && this._isOpen) {
        this.removeAttribute("open");
        this._anchor?.focus();
      }
    }

    _handleFocusOut(e) {
      const anchor = this._anchor;
      const next = e.relatedTarget;
      if (!anchor || !this._isOpen || !next) return;
      if (!this.contains(next) && !anchor.contains(next)) {
        this.removeAttribute("open");
      }
    }

    _openDropdown() {
      this._isOpen = true;
      const dropdown = this.querySelector(".shop-profile-dropdown__menu");
      const avatarBtn = this.querySelector(".shop-profile-dropdown__avatar");

      if (dropdown) dropdown.removeAttribute("hidden");
      if (avatarBtn) avatarBtn.setAttribute("aria-expanded", "true");

      if (this._anchor) {
        this._followAnchor();
        dropdown?.querySelector('[role="menuitem"]')?.focus();
      } else if (this.hasAttribute("anchor")) {
        // Without the trigger the opening click is an outside click and closes the menu
        // again, so a renamed or unrendered anchor would fail silently.
        console.error(
          `[shop-profile-dropdown] anchor not found: ${this.getAttribute("anchor")}`,
        );
      }
    }

    _closeDropdown() {
      this._isOpen = false;
      const dropdown = this.querySelector(".shop-profile-dropdown__menu");
      const avatarBtn = this.querySelector(".shop-profile-dropdown__avatar");

      if (dropdown) dropdown.setAttribute("hidden", "");
      if (avatarBtn) {
        avatarBtn.setAttribute("aria-expanded", "false");
        avatarBtn.focus();
      }

      this._anchor?.setAttribute("aria-expanded", "false");
      this._stopFollowingAnchor();
    }

    // The anchor moves on its own (the sticky nav's pill morphs over 0.64s after a scroll,
    // with no scroll or resize event) and the nav replaces it on re-render, so the menu
    // re-reads the anchor every frame while open.
    _followAnchor() {
      this._syncAnchor();
      cancelAnimationFrame(this._frame);
      this._frame = requestAnimationFrame(() => this._followAnchor());
    }

    _stopFollowingAnchor() {
      cancelAnimationFrame(this._frame);
    }

    _syncAnchor() {
      const anchor = this._anchor;
      const menu = this.querySelector(".shop-profile-dropdown__menu");
      if (!anchor || !menu) return;

      if (anchor.getAttribute("aria-expanded") !== "true") {
        anchor.setAttribute("aria-expanded", "true");
      }
      if (anchor.getAttribute("aria-controls") !== menu.id) {
        anchor.setAttribute("aria-controls", menu.id);
      }
      const rect = anchor.getBoundingClientRect();
      const top = `${rect.bottom + MENU_GAP_PX}px`;
      const right = `${document.documentElement.clientWidth - rect.right}px`;
      if (menu.style.top !== top) menu.style.top = top;
      if (menu.style.right !== right) menu.style.right = right;
    }

    render() {
      const userName = this.getAttribute("user-name") || "";
      const userEmail = this.getAttribute("user-email") || "";
      const accountUrl = this.getAttribute("account-url") || "/account";
      const cashBackHref =
        this.getAttribute("cash-back-href") || "/cash-back/purchases";
      const savedHref = this.getAttribute("saved-href") || "/saved";
      const logoutUrl = this.getAttribute("logout-url") || "/logout";
      const csrfToken = this.getAttribute("csrf-token") || "";
      const avatarButton = this.hasAttribute("anchor")
        ? ""
        : `<button class="shop-profile-dropdown__avatar" type="button" aria-haspopup="menu" aria-expanded="false" aria-label="Open account menu">
          <fragno-avatar${userName ? ` name="${escapeHTML(userName)}"` : ""} size="xs"></fragno-avatar>
        </button>`;

      this.innerHTML = `
        <style>
          shop-profile-dropdown {
            position: absolute;
            top: 0;
            right: 0;
            width: 0;
            height: 0;
            overflow: visible;
          }

          shop-profile-dropdown .shop-profile-dropdown__avatar {
            display: none;
          }

          .shop-profile-dropdown__avatar {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 2.5rem;
            height: 2.5rem;
            padding: 0;
            background: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(255, 255, 255, 0.9);
            border-radius: 9999px;
            box-shadow: 0px 12px 16px -4px rgba(10, 13, 18, 0.08),
                        0px 2px 2px -1px rgba(10, 13, 18, 0.04);
            color: #424242;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .shop-profile-dropdown__avatar fragno-avatar {
            pointer-events: none;
          }

          .shop-profile-dropdown__avatar .fragno-avatar__main {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }

          .shop-profile-dropdown__avatar:hover {
            background: rgba(255, 255, 255, 0.9);
            box-shadow: 0px 16px 20px -4px rgba(10, 13, 18, 0.12),
                        0px 4px 4px -1px rgba(10, 13, 18, 0.06);
          }

          .shop-profile-dropdown__avatar:focus {
            outline: 2px solid var(--colors-primary-default, #0066cc);
            outline-offset: 0.125rem;
          }

          .shop-profile-dropdown__menu {
            position: absolute;
            top: calc(100% + 0.5rem);
            right: 0;
            z-index: 1000;
            min-width: 13.75rem;
          }

          shop-profile-dropdown[anchor] .shop-profile-dropdown__menu {
            position: fixed;
          }

          .shop-profile-dropdown__menu[hidden] {
            display: none;
          }

          .shop-profile-dropdown__content {
            background: var(--colors-white-default, #ffffff);
            border: 1px solid var(--colors-white-default, #ffffff);
            border-radius: var(--radius-4xl, 1.5rem);
            box-shadow: 0px 20px 24px 0px rgba(10, 13, 18, 0.08),
                        0px 8px 8px 0px rgba(10, 13, 18, 0.03),
                        0px 3px 3px 0px rgba(10, 13, 18, 0.04);
            padding: var(--spacing-md, 0.5rem);
            display: flex;
            flex-direction: column;
          }

          .shop-profile-dropdown__email {
            display: flex;
            align-items: center;
            height: 2rem;
            padding: var(--spacing-xs, 0.25rem) var(--spacing-xl, 1rem) var(--spacing-xs, 0.25rem) var(--spacing-md, 0.5rem);
            font-size: var(--font-size-text-xs, 0.75rem);
            font-weight: var(--font-weight-medium, 500);
            line-height: var(--line-height-text-xs, 1.125rem);
            letter-spacing: -0.06px;
            color: var(--colors-gray-600, #525252);
            font-family: var(--font-family-body, 'Inter', sans-serif);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .shop-profile-dropdown__separator {
            display: flex;
            align-items: center;
            height: 1.3125rem;
            padding: 0 var(--spacing-md, 0.5rem);
          }

          .shop-profile-dropdown__separator-line {
            flex: 1;
            height: 1px;
            background: rgba(0, 0, 0, 0.2);
          }

          .shop-profile-dropdown__link {
            display: flex;
            align-items: center;
            gap: var(--spacing-sm, 0.375rem);
            min-height: 2.5rem;
            padding: var(--spacing-md, 0.5rem) var(--spacing-xl, 1rem) var(--spacing-md, 0.5rem) var(--spacing-lg, 0.75rem);
            font-size: var(--font-size-text-sm, 0.875rem);
            font-weight: var(--font-weight-medium, 500);
            line-height: var(--line-height-text-sm, 1.25rem);
            letter-spacing: -0.07px;
            color: var(--colors-gray-900, #141414);
            font-family: var(--font-family-body, 'Inter', sans-serif);
            text-decoration: none;
            border-radius: var(--radius-xl, 0.75rem);
            transition: background 0.15s ease;
            cursor: pointer;
            border: none;
            background: transparent;
            text-align: left;
            width: 100%;
          }

          .shop-profile-dropdown__link:hover {
            background: rgba(0, 0, 0, 0.04);
          }

          .shop-profile-dropdown__link:focus {
            outline: 2px solid var(--colors-primary-default, #0066cc);
            outline-offset: -0.125rem;
          }

          .shop-profile-dropdown__link-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 1.25rem;
            height: 1.25rem;
            flex-shrink: 0;
          }

          .shop-profile-dropdown__link-icon svg {
            width: 1.25rem;
            height: 1.25rem;
          }

          .shop-profile-dropdown__link--logout {
            color: var(--colors-error-700, #B42318);
          }

          .shop-profile-dropdown__link--logout .shop-profile-dropdown__link-icon svg {
            stroke: var(--colors-error-700, #B42318);
          }

          .shop-profile-dropdown__logout-form {
            margin: 0;
            padding: 0;
          }
        </style>

        ${avatarButton}
        <div class="shop-profile-dropdown__menu" id="shop-profile-dropdown-menu" hidden>
          <div class="shop-profile-dropdown__content" role="menu">
            <div class="shop-profile-dropdown__email">${escapeHTML(userEmail)}</div>
            <div class="shop-profile-dropdown__separator"><div class="shop-profile-dropdown__separator-line"></div></div>
            <a href="${escapeHTML(accountUrl)}" class="shop-profile-dropdown__link" role="menuitem">
              <span class="shop-profile-dropdown__link-icon">
                <svg viewBox="0 0 18.3333 15" fill="none" stroke="#141414" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M17.5 4.583H0.833M0.833 7.917H3.789c.45 0 .675 0 .893.039.193.034.381.091.561.17.203.088.39.213.765.463l.484.323c.375.25.562.375.765.463.18.078.368.136.561.17.218.039.443.039.893.039h.911c.45 0 .675 0 .893-.039.193-.034.381-.091.561-.17.203-.088.39-.213.765-.462l.484-.324c.375-.25.562-.375.765-.463.18-.078.368-.135.561-.17.218-.038.443-.038.893-.038H17.5M0.833 3.5v8c0 .933 0 1.4.182 1.757.16.313.415.568.729.728.356.182.823.182 1.756.182h11.333c.934 0 1.4 0 1.757-.182.314-.16.569-.415.729-.728.181-.357.181-.824.181-1.757v-8c0-.933 0-1.4-.181-1.757a1.667 1.667 0 0 0-.729-.728C16.233.833 15.767.833 14.833.833H3.5c-.933 0-1.4 0-1.756.182-.314.16-.569.414-.729.728C.833 2.1.833 2.567.833 3.5z"/>
                </svg>
              </span>
              My Wallet
            </a>
            <a href="${escapeHTML(cashBackHref)}" class="shop-profile-dropdown__link" role="menuitem">
              <span class="shop-profile-dropdown__link-icon">
                <svg viewBox="0 0 18.3333 18.3333" fill="none" stroke="#141414" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M6.25 11.389a1.944 1.944 0 0 0 1.944 1.944H10a2.083 2.083 0 0 0 0-4.166H8.333a2.083 2.083 0 0 1 0-4.167h1.806a2.083 2.083 0 0 1 2.083 1.944M9.167 3.75V5m0 8.333v1.25M17.5 9.167a8.333 8.333 0 1 1-16.667 0 8.333 8.333 0 0 1 16.667 0z"/>
                </svg>
              </span>
              My Cash
            </a>
            <a href="${escapeHTML(savedHref)}" class="shop-profile-dropdown__link" role="menuitem">
              <span class="shop-profile-dropdown__link-icon">
                <svg viewBox="0 0 18.3333 16.6667" fill="none" stroke="#141414" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M12.593.833C15.528.833 17.5 3.627 17.5 6.233c0 5.279-8.185 9.6-8.333 9.6S.833 11.512.833 6.233C.833 3.627 2.806.833 5.74.833c1.686 0 2.788.853 3.426 1.603C9.806 1.686 10.907.833 12.593.833z"/>
                </svg>
              </span>
              Saved
            </a>
            <div class="shop-profile-dropdown__separator" role="separator"><div class="shop-profile-dropdown__separator-line"></div></div>
            <form action="${escapeHTML(logoutUrl)}" method="post" class="shop-profile-dropdown__logout-form">
              <input type="hidden" name="_method" value="delete">
              <input type="hidden" name="authenticity_token" value="${escapeHTML(csrfToken)}">
              <button type="submit" class="shop-profile-dropdown__link shop-profile-dropdown__link--logout" role="menuitem">
                <span class="shop-profile-dropdown__link-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B42318" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                </span>
                Logout
              </button>
            </form>
          </div>
        </div>
      `;
    }
  }

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, ShopProfileDropdown);
  }

  return ShopProfileDropdown;
})();

export { ShopProfileDropdown };
